import { ChatBubbleLeftIcon } from "@heroicons/react/16/solid";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import React, { useState, useEffect } from "react";
import { MdChat, MdChatBubbleOutline } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import ReactSignatureCanvas from "react-signature-canvas";
import Navbar from "../../Navbar/navbar";
import PhoneInput from "../../ui/PhoneInput";
import { baseUrl } from "../../../Environments/environment";
const FORM = {
	title: "Pre Arrival Check In",
	phoneNumber: "Phone Number",
	emailAddress: "Email Address",
	arrivalTime: "Arrival Time",
	vehicleMakeNumber: "Vehicle Make / Number / Color",
	licensePlate: "License Plate",
	specialRequests: "Special Requests",
};
const policies = [
	{
		name: "termsAndConditions",
		label: "I have read and agree to the Terms and Conditions.",
	},
	{
		name: "messageConsent",
		label:
			"Would like to receive promotional offers and exclusive deals from Onelyk via SMS? Message & data rates may apply.",
	},
];
const FormLabel = ({ children, htmlFor }) => (
	<label
		htmlFor={htmlFor}
		className="text-neutral-700 text-sm font-medium mb-1 flex justify-start"
	>
		{children}
	</label>
);
const PreArrivalCheckInTwilio = () => {
	const propertyId = "66a2174cf74f8664b0bb207d";
	const [workflow, setWorkflow] = useState(null);
	const [preArrivalFlow, setPreArrivalFlow] = useState(null);
	const [phoneNumber, setPhoneNumber] = useState("");
	const [isDrawing, setIsDrawing] = useState(false);
	const [signatureData, setSignatureData] = useState(null);
	const sigCanvas = React.useRef(null);
	const [signatureError, setSignatureError] = useState(false);
	const [signature, setSignature] = useState(null); // Store signature as base64 string
	const [email, setEmail] = useState("");
	const [counter, setCounter] = useState(0); // To force re-render
	const formRef = React.useRef(null);
	const [arrivalTime, setArrivalTime] = useState("");
	const [countryCode, setCountryCode] = useState("US +1");
	const [vehicleInfo, setVehicleInfo] = useState("");
	const [licensePlate, setLicensePlate] = useState("");
	const [specialRequests, setSpecialRequests] = useState("");
	const [acceptedPolicies, setAcceptedPolicies] = useState([]);
	const navigate = useNavigate();

	const handleBegin = () => {
		setIsDrawing(true);
		setSignatureError(false); // Clear error when user starts drawing
	};
	const clearSignature = () => {
		if (sigCanvas) {
			sigCanvas.current.clear();
			setSignature(null); // Clear signature from state

			setIsDrawing(false);
			setCounter(counter + 1); // Force re-render
			setSignatureError(false); // Clear error on clear
		}
	};

	const saveSignatureToState = () => {
		if (sigCanvas.current) {
			const dataUrl = sigCanvas.current.toDataURL();
			setSignature(dataUrl); // Save as base64 string
		}
	};

	// Helper: convert data URL to Blob
	const dataUrlToBlob = (dataUrl) => {
		try {
			const parts = dataUrl.split(",");
			const mime = parts[0].match(/:(.*?);/)[1];
			const bstr = atob(parts[1]);
			let n = bstr.length;
			const u8arr = new Uint8Array(n);
			while (n--) {
				u8arr[n] = bstr.charCodeAt(n);
			}
			return new Blob([u8arr], { type: mime });
		} catch (e) {
			return null;
		}
	};

	// Ref callback to restore as soon as canvas mounts
	const handleSigCanvasRef = (ref) => {
		sigCanvas.current = ref;
		if (
			ref &&
			signature &&
			typeof signature === "string" &&
			signature.startsWith("data:image")
		) {
			try {
				ref.fromDataURL(signature);
			} catch (e) {
				// ignore if invalid
			}
		}
	};
	const validatePolicies = () => {
		const checkboxes = document.querySelectorAll('input[type="checkbox"]');
		for (let i = 0; i < checkboxes.length; i++) {
			if (!checkboxes[i].checked) {
				return false;
			}
		}
		return true;
	};

	// Restore signature from state if it exists
	useEffect(() => {
		if (
			sigCanvas.current &&
			signature &&
			typeof signature === "string" &&
			signature.startsWith("data:image")
		) {
			try {
				sigCanvas.current.fromDataURL(signature);
			} catch (e) {
				// ignore if invalid
			}
		}
		// eslint-disable-next-line
	}, [signature, counter]);

	// On mobile, canvas can be cleared on scroll/orientation/resize/visibility changes.
	// Rehydrate from state if canvas becomes empty.
	useEffect(() => {
		const restoreIfEmpty = () => {
			try {
				if (
					sigCanvas.current &&
					typeof sigCanvas.current.isEmpty === "function" &&
					sigCanvas.current.isEmpty() &&
					signature &&
					typeof signature === "string" &&
					signature.startsWith("data:image")
				) {
					sigCanvas.current.fromDataURL(signature);
				}
			} catch (e) {
				// ignore
			}
		};

		window.addEventListener("scroll", restoreIfEmpty, { passive: true });
		window.addEventListener("resize", restoreIfEmpty);
		window.addEventListener("orientationchange", restoreIfEmpty);
		document.addEventListener("visibilitychange", restoreIfEmpty);

		return () => {
			window.removeEventListener("scroll", restoreIfEmpty);
			window.removeEventListener("resize", restoreIfEmpty);
			window.removeEventListener("orientationchange", restoreIfEmpty);
			document.removeEventListener("visibilitychange", restoreIfEmpty);
		};
	}, [signature]);
	const checkEmail = (email) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};
	const fileInputRef = React.useRef(null);

	const handleSubmit = async () => {
		try {
			if (!formRef.current) return;
			if (!formRef.current.checkValidity()) {
				console.log("form invalid");
				formRef.current.reportValidity();
			}
			const canvas = sigCanvas.current.getTrimmedCanvas();
			const isEmpty = sigCanvas.current.isEmpty(); // Check if the canvas is empty

			if (isEmpty) {
				// Canvas is empty, show error
				validatePolicies();
				checkEmail(email);
				setSignatureError(true);
				return;
			}

			const signatureData = canvas.toDataURL("image/png");
			setSignature(signatureData); // Save base64 string to state

			// Convert base64 to Blob
			const byteString = atob(signatureData.split(",")[1]);
			const mimeString = signatureData
				.split(",")[0]
				.split(":")[1]
				.split(";")[0];

			const arrayBuffer = new ArrayBuffer(byteString.length);
			const uint8Array = new Uint8Array(arrayBuffer);

			for (let i = 0; i < byteString.length; i++) {
				uint8Array[i] = byteString.charCodeAt(i);
			}

			const blob = new Blob([uint8Array], { type: mimeString });
			// setSignature(blob); // Don't overwrite base64 string in state
			const formData = new FormData();
			formData.append("propertyId", propertyId);
			formData.append("phoneNumber", phoneNumber);
			formData.append("email", email);
			formData.append("arrivalTime", arrivalTime);
			formData.append("vehicleInfo", vehicleInfo);
			formData.append("licensePlate", licensePlate);
			formData.append("specialRequests", specialRequests);
			formData.append("signature", blob, "signature.png");
			formData.append(
				"countryCode",
				countryCode.split(" ")[countryCode.split(" ").length - 1],
			);
			policies.forEach((policy) =>
				formData.append(policy.name, acceptedPolicies.includes(policy.name)),
			);
			const response = await axios.post(
				`${baseUrl}/guestApi/property/${propertyId}/preArrival`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			);
			navigate(`/${response.data.result.guestSession._id}`);
		} catch (error) {
			console.error("Error submitting form:", error);
		}
	};

	useEffect(() => {
		const getWorkflows = async () => {
			try {
				const response = await axios.get(
					`${baseUrl}/guestApi/property/${propertyId}/workflow`,
				);
				setPreArrivalFlow(response.data.preArrivalFlow);
			} catch (error) {
				console.error("Error fetching workflows:", error);
			}
		};
		getWorkflows();
	}, [propertyId]);
	const handleIconClick = () => {
		fileInputRef.current.click();
	};
	return (
		<>
			<div className="bg-gray-900 flex justify-between items-center p-2">
				<Link to="/" className="bg-neutral-100 px-2 py-1 rounded-lg text-xs">
					{" "}
					<div className="flex items-center gap-1">
						<ArrowLeft
							className="
              h-4 w-4 text-gray-700
              "
						/>
						Home
					</div>
				</Link>
				<MdChatBubbleOutline className="h-6 w-6 text-white" />
			</div>
			<div className="bg-gray-900 min-h-screen flex flex-col justify-between items-center">
				<div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm mx-6">
					<div className="text-gray-400 flex justify-between">
						<div>
							<div className="text-lg font-medium mb-6">Online Check In</div>
						</div>
					</div>
					<form ref={formRef}>
						<div
							className="mb-4"
							ref={formRef}
							onSubmit={(e) => handleSubmit(e)}
						>
							<div className="flex flex-col">
								<PhoneInput
									countryCodes={["+1", "+91"]}
									onCountryCodeChange={(e) => setCountryCode(e.target.value)}
									onPhoneNumberChange={(e) => {
										setPhoneNumber(e.target.value);
									}}
									countryCode={countryCode}
									phoneNumber={phoneNumber}
								/>
							</div>
						</div>
						<div className="mb-4">
							<FormLabel htmlFor="email">Email Address</FormLabel>
							<input
								type="email"
								id="email"
								name="email"
								className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
						</div>
						<div>
							<FormLabel htmlFor="arrivalTime">Arrival Time</FormLabel>
							<input
								type="time"
								id="arrivalTime"
								name="arrivalTime"
								className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
								required={true}
								value={arrivalTime}
								onChange={(e) => setArrivalTime(e.target.value)}
							/>
						</div>
						<div className="mb-4 flex gap-2">
							<div>
								<FormLabel htmlFor="vehicleInfo">
									Vehicle Make / Number / Color
								</FormLabel>
								<input
									type="text"
									id="vehicleInfo"
									name="vehicleInfo"
									value={vehicleInfo}
									onChange={(e) => setVehicleInfo(e.target.value)}
									className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
								/>
							</div>
							<div>
								<FormLabel htmlFor="">Licnense Plate #</FormLabel>
								<input
									type="text"
									className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
									value={licensePlate}
									onChange={(e) => setLicensePlate(e.target.value)}
								/>
							</div>
						</div>
						<div>
							<FormLabel htmlFor="specialRequests">Special Requests</FormLabel>
							<textarea
								id="specialRequests"
								name="specialRequests"
								value={specialRequests}
								onChange={(e) => setSpecialRequests(e.target.value)}
								className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
								rows={3}
							></textarea>
						</div>
						<div className="my-4">
							{policies.map((policy, index) => (
								<div key={policy.name} className="flex items-center mb-2">
									<input
										type="checkbox"
										id={policy.name}
										name={policy.name}
										className="mr-2 leading-tight"
										onChange={(e) => {
											if (e.target.checked) {
												setAcceptedPolicies([...acceptedPolicies, policy.name]);
											} else {
												setAcceptedPolicies(
													acceptedPolicies.filter((p) => p !== policy.name),
												);
											}
										}}
									/>
									<label
										htmlFor={policy.name}
										className="text-sm text-gray-700"
									>
										{policy.label}
									</label>
								</div>
							))}
						</div>
						<div className="relative">
							{!isDrawing && (
								<div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 pointer-events-none">
									<span className="text-lg">SIGNATURE BOARD</span>
									<span className="text-xs pt-5">
										Use your mouse or finger to draw your signature
									</span>
								</div>
							)}
							<ReactSignatureCanvas
								ref={handleSigCanvasRef}
								penColor="black"
								onBegin={handleBegin}
								onEnd={saveSignatureToState}
								canvasProps={{
									className: "w-full h-48 border border-gray-400 rounded-md",
								}}
							/>
							<button
								type="button"
								onClick={clearSignature}
								className="absolute top-2 right-2 text-rose-500 px-3 py-1 "
							>
								clear
							</button>
						</div>
						{signatureError && (
							<p className="text-red-500 text-sm mt-2">
								Please provide your signature.
							</p>
						)}
					</form>
				</div>
				<div className="flex justify-center w-full bg-gray-700 items-center"></div>
			</div>
			<button
				type="button"
				onClick={handleSubmit}
				className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 border-neutral-200 border-2 text-white font-medium rounded focus:outline-none focus:shadow-outline px-2 py-1"
			>
				{" "}
				Submit
			</button>
		</>
	);
};
export default PreArrivalCheckInTwilio;
